import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { Col, Row } from "reactstrap";
import { Table } from "antd";
import "antd/dist/antd.css";
import { Search, Grid as Columns, ChevronDown, Edit } from "react-feather";
import Loader from "../../../../components/Loader/Loader";
import { getCompanySettingServices } from "../../../../actions/companyActions";
import {
  getCustomers,
  createCustomer,
  deleteCustomer,
  updateCustomer,
  getCustomersSummary,
} from "../../../../actions/admin/crm-actions";
import isEmpty from "../../../../validation/is-empty";
import { notification } from "../../../../utils/utility";
import PaginatorDropdown from "../../../../components/Datatable/PaginationDropdown";
import ActionDots from "../../../../components/Datatable/ActionDots";
import CreateModal from "./create";
import EditModal from "./edit";
import DeleteModal from "./delete";
import CustomerProfile from "./profile";
import { Dropdown, Menu } from "antd";
import "./customer.scss";

/* ─── Avatar color palette (matches reference site) ─────────────── */
const AVATAR_PALETTE = [
  { bg: "#e2f0ff", color: "#2868a0" },   // blue
  { bg: "#eee7ff", color: "#6b4db1" },   // violet
  { bg: "#ffeed8", color: "#c66b04" },   // orange
  { bg: "#e2f6ea", color: "#238854" },   // green
  { bg: "#ffe6ef", color: "#aa4167" },   // pink
  { bg: "#dff6f5", color: "#187d79" },   // teal
  { bg: "#e2edf5", color: "#295b78" },   // navy
  { bg: "#fff3cd", color: "#9b7411" },   // gold
];

const getAvatarColor = (firstName = "", lastName = "") => {
  const str = (firstName + lastName).toLowerCase();
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

/* ─── helpers ──────────────────────────────────────────────────── */
const getInitials = (firstName = "", lastName = "") =>
  `${(firstName[0] || "").toUpperCase()}${(lastName[0] || "").toUpperCase()}`;

const formatCurrency = (v) =>
  v !== null && v !== undefined
    ? `$${parseFloat(v).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
    : "—";

const CustomerAvatar = ({ firstName = "", lastName = "", email = "" }) => {
  const initials = getInitials(firstName, lastName) || (email[0] || "?").toUpperCase();
  const { bg, color } = getAvatarColor(firstName, lastName);
  return (
    <div className="crm-avatar" style={{ background: bg, color }}>
      {initials}
    </div>
  );
};

/* ─── Summary Card ──────────────────────────────────────────────── */
const SummaryCard = ({ label, value, sub }) => (
  <div className="crm-summary-card">
    <p className="crm-summary-card__label">{label}</p>
    <p className="crm-summary-card__value">{value ?? "—"}</p>
    {sub && <p className="crm-summary-card__sub">{sub}</p>}
  </div>
);

/* ── default visible columns ─────────────────────────────────────── */
const DEFAULT_VISIBLE = {
  phone:          true,
  last_booking:   true,
  next_booking:   true,
  total_bookings: true,
  total_spent:    false,
  status:         false,
  assigned_staff: false,
  country:        false,
  city:           false,
  zip:            false,
};

/* ══════════════════════════════════════════════════════════════════
   CustomerListing
   ══════════════════════════════════════════════════════════════════ */
const CustomerListing = (props) => {
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: false,
  });

  const [data, setData]           = useState([]);
  const [countries, setCountries] = useState([]);
  const [summary, setSummary]     = useState(null);

  const [searchValue, setSearchValue] = useState("");
  const searchTimeoutRef = useRef(null);

  const [visibleCols, setVisibleCols] = useState({ ...DEFAULT_VISIBLE });
  const [showColPicker, setShowColPicker] = useState(false);
  const colPickerRef = useRef(null);

  const [rowData, setRowData]             = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isProfileOpen, setIsProfileOpen]       = useState(false);

  /* ── fetch summary ── */
  const fetchSummary = useCallback(() => {
    props.getCustomersSummary({
      onSuccess: (data) => setSummary(data),
      onError:   () => {},
    });
  }, []);

  /* ── fetch table ── */
  const fetchTableData = useCallback((params = {}) => {
    setIsDataLoading(true);
    let sortField = "id", sortOrder = "asc";
    if (!isEmpty(params.sortField) && !isEmpty(params.sortOrder)) {
      sortField = params.sortField;
      sortOrder = params.sortOrder === "ascend" ? "asc" : "desc";
    }
    props.getCustomers({
      data: {
        pageSize:   params.pagination?.pageSize   ?? pagination.pageSize,
        pageNumber: params.pagination?.current    ?? pagination.current,
        sortField,
        sortOrder,
        filter: { search: params.search ?? searchValue },
      },
      onSuccess: (data) => {
        setData(data.entity || []);
        setPagination((prev) => ({ ...prev, ...params.pagination, total: data.totalCount }));
        setIsDataLoading(false);
      },
      onError: () => { setData([]); setIsDataLoading(false); },
    });
  }, [searchValue]);

  /* ── initial load ── */
  useEffect(() => {
    fetchTableData({ pagination });
    fetchSummary();
    props.getCompanySettingServices({
      data: {},
      onSuccess: (d) => setCountries(d.countries || []),
      onError:   () => {},
    });
    const handleOutside = (e) => {
      if (colPickerRef.current && !colPickerRef.current.contains(e.target))
        setShowColPicker(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  /* ── search ── */
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchValue(val);
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchTableData({ pagination: { ...pagination, current: 1 }, search: val });
    }, 350);
  };

  /* ── table change ── */
  const handleTableChange = (pg, _f, sorter) => {
    fetchTableData({ sortField: sorter.field, sortOrder: sorter.order, pagination: pg });
  };

  /* ── page size ── */
  const onPageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    fetchTableData({ pagination: { current: 1, pageSize: newSize, total: pagination.total } });
  };

  /* ── CRUD ── */
  const onAddRecord = (formData) => {
    setIsPageLoading(true); setIsDataLoading(true);
    props.createCustomer({
      data: formData,
      onSuccess: (res) => {
        if (res.success) notification.success("Customer successfully added");
        else notification.error("An error occurred, please try later.");
        fetchTableData({ pagination }); fetchSummary();
        setIsPageLoading(false); setIsDataLoading(false);
      },
      onError: () => { setIsPageLoading(false); setIsDataLoading(false); },
    });
  };

  const onUpdateRecord = (formData) => {
    setIsPageLoading(true); setIsDataLoading(true);
    props.updateCustomer({
      data: formData,
      onSuccess: (res) => {
        if (res.success) notification.success("Customer successfully updated");
        else notification.error("An error occurred, please try later.");
        fetchTableData({ pagination });
        setIsPageLoading(false); setIsDataLoading(false);
        if (isProfileOpen && selectedCustomer?.id === formData.id)
          setSelectedCustomer((prev) => ({ ...prev, ...formData }));
      },
      onError: () => { setIsPageLoading(false); setIsDataLoading(false); },
    });
  };

  const onDeleteRecord = (id) => {
    setIsDataLoading(true);
    props.deleteCustomer({
      data: { id },
      onSuccess: () => {
        notification.success("Record successfully deleted");
        fetchTableData({ pagination: { ...pagination, current: 1 } });
        fetchSummary();
        if (isProfileOpen && selectedCustomer?.id === id) setIsProfileOpen(false);
        setIsDataLoading(false);
      },
      onError: () => {
        notification.error("An error occurred during delete, please try later");
        setIsDataLoading(false);
      },
    });
  };

  /* ── profile panel ── */
  const openProfile  = (record) => { setSelectedCustomer(record); setIsProfileOpen(true); };
  const closeProfile = () => { setIsProfileOpen(false); setSelectedCustomer(null); };

  /* ── all columns definition ── */
  const allColumns = [
    {
      key: "customer", title: "Customer", dataIndex: "first_name", always: true,
      render: (_, rec) => (
        <div className="crm-customer-cell" style={{ cursor: "pointer" }}
          onClick={(e) => { e.stopPropagation(); openProfile(rec); }}>
          <CustomerAvatar firstName={rec.first_name} lastName={rec.last_name} email={rec.email} />
          <div className="crm-customer-cell__info">
            <span className="crm-customer-cell__name">{rec.first_name} {rec.last_name}</span>
            {rec.email && <span className="crm-customer-cell__email">{rec.email}</span>}
          </div>
        </div>
      ),
    },
    {
      key: "phone", title: "Phone", dataIndex: "phone", sorter: true,
      render: (v) => v || <span className="crm-none">—</span>,
    },
    {
      key: "last_booking", title: "Last booking", dataIndex: "last_booking_date",
      render: (v) => v || <span className="crm-none">None</span>,
    },
    {
      key: "next_booking", title: "Next booking", dataIndex: "next_booking_date",
      render: (v) => v
        ? <span className="crm-next-booking">{v}</span>
        : <span className="crm-none">None</span>,
    },
    {
      key: "total_bookings", title: "Total bookings", dataIndex: "total_bookings",
      render: (v) => <span className="crm-bookings-badge">{v ?? 0}</span>,
    },
    {
      key: "total_spent", title: "Total spent", dataIndex: "total_spent",
      render: (v) => formatCurrency(v),
    },
    {
      key: "status", title: "Status", dataIndex: "status",
      render: (v) => {
        const isActive = v === "E";
        return (
          <span className={`crm-status-badge crm-status-badge--${isActive ? "active" : "inactive"}`}>
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      key: "assigned_staff", title: "Assigned staff", dataIndex: "assigned_staff_name",
      render: (v) => v || <span className="crm-none">—</span>,
    },
    {
      key: "country", title: "Country", dataIndex: "country",
      render: (v) => (v && v.label) ? v.label : <span className="crm-none">—</span>,
    },
    {
      key: "city", title: "City", dataIndex: "city",
      render: (v) => v || <span className="crm-none">—</span>,
    },
    {
      key: "zip", title: "ZIP", dataIndex: "zip",
      render: (v) => v || <span className="crm-none">—</span>,
    },
    {
      key: "actions", title: "", dataIndex: "actions", always: true, width: 60,
      render: (_, rec) => {
        const menu = (
          <Menu items={[
            {
              label: (
                <span onClick={(e) => { e.stopPropagation(); setRowData(rec); setShowEditModal(true); }}>
                  <Edit size={14} className="mr-1" /> Edit
                </span>
              ), key: "edit",
            },
            { type: "divider" },
            { label: <DeleteModal onDeleteRecord={onDeleteRecord} data={rec} />, key: "delete" },
          ]} />
        );
        return (
          <div className="actions cursor-pointer" onClick={(e) => e.stopPropagation()}>
            <Dropdown overlay={menu} trigger={["click"]}>
              <a onClick={(e) => e.preventDefault()}><ActionDots /></a>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  const visibleColumns = allColumns.filter((c) => c.always || visibleCols[c.key]);

  return (
    <>
      <Loader isShowLoader={isPageLoading} />

      {showEditModal && (
        <EditModal
          onUpdateRecord={onUpdateRecord}
          data={rowData}
          countries={countries}
          showEditModal={showEditModal}
          handleClose={() => setShowEditModal(false)}
        />
      )}

      {/* ── Page header ── */}
      <div className="crm-page-header">
        <div className="crm-page-header__left">
          <span className="crm-page-header__crumb">CRM</span>
          <h1 className="crm-page-header__title">Customers</h1>
          <p className="crm-page-header__sub">
            Manage customer contact details and review their booking history.
          </p>
        </div>
        <div className="crm-page-header__actions">
          <CreateModal onAddRecord={onAddRecord} countries={countries} />
        </div>
      </div>

      {/* ── Summary cards (separate bordered cards) ── */}
      <div className="crm-summary-row">
        <SummaryCard
          label="Total customers"
          value={summary?.total_customers ?? "—"}
        />
        <SummaryCard
          label="New this month"
          value={summary?.new_this_month ?? "—"}
        />
        <SummaryCard
          label="Returning customers"
          value={summary?.returning_customers ?? "—"}
        />
        <SummaryCard
          label="Total revenue"
          value={summary ? formatCurrency(summary.total_revenue) : "—"}
        />
      </div>

      {/* ── Toolbar card (separate) ── */}
      <div className="crm-toolbar-card">
        <div className="crm-toolbar">
          <label className="crm-search-box">
            <Search size={15} className="crm-search-box__icon" aria-hidden="true" />
            <input
              type="search"
              className="crm-search-box__input"
              placeholder="Search by name, email or phone..."
              value={searchValue}
              onChange={handleSearchChange}
              autoComplete="off"
            />
          </label>

          <div className="crm-toolbar__right">
            {pagination.total > 0 && (
              <span className="crm-customer-count">{pagination.total} customer{pagination.total !== 1 ? "s" : ""}</span>
            )}

            <div className="crm-col-picker-wrap" ref={colPickerRef}>
              <button
                className={`crm-toolbar-btn${showColPicker ? " crm-toolbar-btn--active" : ""}`}
                onClick={() => setShowColPicker((v) => !v)}
              >
                <Columns size={14} className="mr-1" /> Columns <ChevronDown size={12} className="ml-1" />
              </button>
              {showColPicker && (
                <div className="crm-col-picker">
                  <p className="crm-col-picker__title">Show / hide columns</p>
                  {Object.keys(DEFAULT_VISIBLE).map((key) => {
                    const label = {
                      phone: "Phone", last_booking: "Last booking",
                      next_booking: "Next booking", total_bookings: "Total bookings",
                      total_spent: "Total spent", status: "Status",
                      assigned_staff: "Assigned staff",
                      country: "Country", city: "City", zip: "ZIP",
                    }[key];
                    return (
                      <label key={key} className="crm-col-picker__item">
                        <input
                          type="checkbox"
                          checked={!!visibleCols[key]}
                          onChange={() =>
                            setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }))
                          }
                        />
                        {label}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Table card (separate) ── */}
      <div className="crm-content-card">
        <Table
          columns={visibleColumns}
          className="crm_customer_table"
          rowKey={(r) => r.id}
          dataSource={data}
          pagination={pagination}
          loading={isDataLoading}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => openProfile(record),
            className: isProfileOpen && selectedCustomer?.id === record.id ? "crm-selected-row" : "",
            style: { cursor: "pointer" },
          })}
          locale={{
            emptyText: (
              <div className="crm-empty-state">
                <span className="crm-empty-state__icon">👥</span>
                <p className="crm-empty-state__text">
                  {searchValue
                    ? "No customers match your search."
                    : "No customers yet. Add your first customer to get started."}
                </p>
              </div>
            ),
          }}
        />

        <div className="crm-pagination-footer">
          <PaginatorDropdown
            onPageSizeChange={onPageSizeChange}
            pageSize={pagination.pageSize}
          />
        </div>
      </div>

      {/* ── Customer Profile Panel ── */}
      {isProfileOpen && selectedCustomer && (
        <CustomerProfile
          customer={selectedCustomer}
          onClose={closeProfile}
          onEdit={(rec) => { setRowData(rec); setShowEditModal(true); }}
        />
      )}
    </>
  );
};

/* ── redux ── */
const mapStateToProps = (state) => ({ filter: state.filter });

export default connect(mapStateToProps, {
  getCompanySettingServices,
  getCustomers,
  createCustomer,
  deleteCustomer,
  updateCustomer,
  getCustomersSummary,
})(CustomerListing);
